import { useState, useEffect, useRef } from "react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Terminal,
  X,
  Zap,
  Sparkles,
  Database,
  Inbox,
  RefreshCcw,
  Bug,
} from "lucide-react";

/**
 * DevConsole — isolated developer HUD.
 * • Only mounts when NODE_ENV !== "production" OR ?dev=1 is in the URL.
 * • Uses an axios request-adapter shim to short-circuit /api/* responses
 *   with mock data. Nothing on the backend / db / webhook is touched.
 */

const MOCK_USER = {
  user_id: "dev-user",
  email: "dev@receiptsheets.com",
  name: "Dev Mode",
  picture: null,
  subscription_status: "inactive",
  subscription_tier: null,
  stripe_customer_id: null,
  stripe_subscription_id: null,
  forwarding_alias: "devmode@receiptsheets.com",
  created_at: new Date().toISOString(),
};

const MOCK_RECEIPTS = [
  { id: "dev-r-1", merchant: "Starbucks Reserve", date: "2026-02-18", subtotal: 14.20, tax: 1.14, total: 15.34, currency: "USD", category: "meals", tags: ["client-meeting"], tax_deductible: true, line_items: [{ description: "Cortado", quantity: 1, price: 5.75 }, { description: "Almond Croissant", quantity: 1, price: 4.95 }, { description: "Cold Brew", quantity: 1, price: 4.50 }], source: "upload", created_at: new Date().toISOString() },
  { id: "dev-r-2", merchant: "Amazon.com", date: "2026-02-15", subtotal: 227.98, tax: 18.24, total: 246.22, currency: "USD", category: "office_supplies", tags: ["office"], tax_deductible: true, line_items: [{ description: "Anker 8-port USB-C hub", quantity: 1, price: 78.99 }, { description: "Herman Miller mouse pad", quantity: 1, price: 49.99 }, { description: "Standing desk mat", quantity: 1, price: 99.00 }], source: "amazon", created_at: new Date().toISOString() },
  { id: "dev-r-3", merchant: "Home Depot", date: "2026-02-11", subtotal: 342.10, tax: 27.37, total: 369.47, currency: "USD", category: "office_supplies", tags: ["office-remodel"], tax_deductible: true, line_items: [{ description: "Cordless drill kit", quantity: 1, price: 199.00 }, { description: "LED shop lights", quantity: 4, price: 22.50 }, { description: "Extension cord 50ft", quantity: 1, price: 53.10 }], source: "upload", created_at: new Date().toISOString() },
  { id: "dev-r-4", merchant: "DoorDash · Sushi Nakamura", date: "2026-02-09", subtotal: 68.44, tax: 5.48, total: 82.42, currency: "USD", category: "meals", tags: ["team-lunch"], tax_deductible: true, line_items: [{ description: "Omakase set (x2)", quantity: 2, price: 32.00 }, { description: "Delivery + tip", quantity: 1, price: 8.92 }], source: "doordash", created_at: new Date().toISOString() },
  { id: "dev-r-5", merchant: "Marriott Downtown", date: "2026-02-05", subtotal: 512.55, tax: 51.26, total: 563.81, currency: "USD", category: "travel", tags: ["conference"], tax_deductible: true, line_items: [{ description: "Room · 1 night suite", quantity: 1, price: 429.00 }, { description: "Valet parking", quantity: 1, price: 45.00 }, { description: "In-room dining", quantity: 1, price: 38.55 }], source: "upload", created_at: new Date().toISOString() },
];

const BREVO_MOCK_RECEIPT = {
  id: "dev-r-brevo-" + Math.random().toString(36).slice(2, 8),
  merchant: "Adobe Creative Cloud",
  date: new Date().toISOString().slice(0, 10),
  subtotal: 54.99,
  tax: 0,
  total: 54.99,
  currency: "USD",
  category: "software",
  tags: ["subscription"],
  tax_deductible: true,
  line_items: [{ description: "Creative Cloud All Apps", quantity: 1, price: 54.99 }],
  source: "email",
  created_at: new Date().toISOString(),
};

// ---------- axios mock shim ----------
let mockState = {
  active: false,
  receipts: [],
  extraReceipts: [],
};

function applyMocks() {
  const all = () => [...mockState.extraReceipts, ...mockState.receipts];
  window.__RS_DEV_INTERCEPTOR__ = api.interceptors.request.use((config) => {
    if (!mockState.active) return config;
    const url = (config.url || "").replace(/^\//, "");
    // Match specific endpoints
    const respond = (data) => {
      config.adapter = () =>
        Promise.resolve({ data, status: 200, statusText: "OK", headers: {}, config, request: {} });
      return config;
    };
    if (url === "receipts" && (!config.method || config.method === "get")) return respond(all());
    if (/^receipts\/([^/]+)$/.test(url) && (!config.method || config.method === "get")) {
      const id = url.split("/")[1];
      const r = all().find((x) => x.id === id);
      return respond(r || {});
    }
    if (url === "stats/summary") {
      const list = all();
      const total_spent = list.reduce((s, r) => s + Number(r.total || 0), 0);
      const deductible = list.filter((r) => r.tax_deductible).reduce((s, r) => s + Number(r.total || 0), 0);
      const tax_paid = list.reduce((s, r) => s + Number(r.tax || 0), 0);
      const by_category = list.reduce((m, r) => ({ ...m, [r.category]: (m[r.category] || 0) + Number(r.total || 0) }), {});
      const monthKey = new Date().toISOString().slice(0, 7);
      const this_month = list.filter((r) => (r.date || "").startsWith(monthKey)).reduce((s, r) => s + Number(r.total || 0), 0);
      return respond({
        receipt_count: list.length,
        total_spent: +total_spent.toFixed(2),
        deductible: +deductible.toFixed(2),
        tax_paid: +tax_paid.toFixed(2),
        this_month: +this_month.toFixed(2),
        by_category: Object.fromEntries(Object.entries(by_category).map(([k, v]) => [k, +v.toFixed(2)])),
      });
    }
    if (url === "insights/leaks") return respond([]);
    if (url === "bank/transactions") return respond([]);
    if (url === "auth/me") {
      const u = window.__RS_DEV_USER__ || MOCK_USER;
      return respond(u);
    }
    return config;
  });
}
function removeMocks() {
  if (window.__RS_DEV_INTERCEPTOR__ != null) {
    api.interceptors.request.eject(window.__RS_DEV_INTERCEPTOR__);
    window.__RS_DEV_INTERCEPTOR__ = null;
  }
  mockState = { active: false, receipts: [], extraReceipts: [] };
  window.__RS_DEV_USER__ = null;
}

// ---------- Component ----------
export default function DevConsole() {
  // Production lockdown: DevConsole is COMPLETELY blocked when NODE_ENV === 'production',
  // regardless of any ?dev=1 URL flag. This prevents accidental exposure of the auth-bypass
  // + tier-elevation + seed-injection HUD on public deployments.
  const isProd = typeof process !== "undefined" && process.env.NODE_ENV === "production";
  const isDev = !isProd && (
    (typeof process !== "undefined" && process.env.NODE_ENV !== "production") ||
    (typeof window !== "undefined" && new URLSearchParams(window.location.search).get("dev") === "1")
  );

  const { user, setUser, setLoading } = useAuth();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(null);
  const installed = useRef(false);

  useEffect(() => {
    if (!isDev) return;
    if (!installed.current) {
      applyMocks();
      installed.current = true;
    }
    return () => {
      if (installed.current) removeMocks();
    };
  }, [isDev]);

  if (!isDev) return null;

  const bypassAuth = () => {
    setBusy("auth");
    mockState.active = true;
    window.__RS_DEV_USER__ = { ...MOCK_USER };
    if (typeof setLoading === "function") setLoading(false);
    setUser({ ...MOCK_USER });
    toast.success("Dev auth bypass — logged in as Dev Mode");
    setBusy(null);
  };

  const activateBusiness = () => {
    setBusy("business");
    mockState.active = true;
    const upgraded = {
      ...(window.__RS_DEV_USER__ || user || MOCK_USER),
      subscription_status: "active",
      subscription_tier: "business",
    };
    window.__RS_DEV_USER__ = upgraded;
    setUser(upgraded);
    toast.success("Business tier active — paywall bypassed");
    setBusy(null);
  };

  const populateDashboard = () => {
    setBusy("populate");
    mockState.active = true;
    mockState.receipts = MOCK_RECEIPTS;
    if (!window.__RS_DEV_USER__) {
      window.__RS_DEV_USER__ = { ...MOCK_USER, subscription_status: "active", subscription_tier: "business" };
      setUser(window.__RS_DEV_USER__);
    }
    toast.success(`Injected ${MOCK_RECEIPTS.length} mock receipts`);
    // Nudge pages that already fetched by dispatching a soft event
    window.dispatchEvent(new Event("rs-dev-refresh"));
    setBusy(null);
  };

  const simulateBrevo = () => {
    setBusy("brevo");
    mockState.active = true;
    const newReceipt = { ...BREVO_MOCK_RECEIPT, id: "dev-r-brevo-" + Math.random().toString(36).slice(2, 8) };
    mockState.extraReceipts = [newReceipt, ...mockState.extraReceipts];
    if (!window.__RS_DEV_USER__) {
      window.__RS_DEV_USER__ = { ...MOCK_USER, subscription_status: "active", subscription_tier: "business" };
      setUser(window.__RS_DEV_USER__);
    }
    toast.success(`Brevo inbound simulated — "${newReceipt.merchant}" ingested via email`);
    window.dispatchEvent(new Event("rs-dev-refresh"));
    setBusy(null);
  };

  const resetState = () => {
    setBusy("reset");
    removeMocks();
    installed.current = false;
    setUser(null);
    if (typeof setLoading === "function") setLoading(false);
    toast.success("Dev state reset — reboot the app to test onboarding");
    setTimeout(() => {
      applyMocks();
      installed.current = true;
      setBusy(null);
      // Send them back to the landing so onboarding restarts cleanly
      window.location.replace("/");
    }, 250);
  };

  const ACTIONS = [
    { key: "auth", label: "Bypass Auth", icon: Zap, run: bypassAuth, hint: "Fake Google login" },
    { key: "business", label: "Activate Business Status", icon: Sparkles, run: activateBusiness, hint: "Skip Stripe paywall" },
    { key: "populate", label: "Populate Dashboard", icon: Database, run: populateDashboard, hint: "5 stylized mock receipts" },
    { key: "brevo", label: "Simulate Brevo Inbound", icon: Inbox, run: simulateBrevo, hint: "Trigger email-ingest path" },
    { key: "reset", label: "Reset State", icon: RefreshCcw, run: resetState, hint: "Wipe → replay onboarding" },
  ];

  return (
    <>
      {/* Floating trigger */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          data-testid="devops-toggle"
          aria-label="Dev Ops Console"
          className="fixed top-4 right-4 flex items-center gap-2 rounded-lg px-3 h-9 font-mono text-xs uppercase tracking-widest text-emerald-300 hover:text-emerald-200 transition-colors"
          style={{
            zIndex: 10000,
            background: "rgba(2,6,23,0.6)",
            backdropFilter: "blur(10px)",
            WebkitBackdropFilter: "blur(10px)",
            border: "1px solid rgba(16,185,129,0.4)",
            boxShadow: "0 0 20px rgba(16,185,129,0.35)",
          }}
        >
          <Bug size={13} /> Dev Ops Console
        </button>
      )}

      {/* Drawer */}
      {open && (
        <div
          data-testid="devops-drawer"
          className="fixed top-4 right-4 w-[320px] rounded-2xl overflow-hidden flex flex-col"
          style={{
            zIndex: 10000,
            background: "rgba(2,6,23,0.9)",
            backdropFilter: "blur(24px)",
            WebkitBackdropFilter: "blur(24px)",
            border: "1px solid rgba(16,185,129,0.4)",
            boxShadow:
              "0 0 0 1px rgba(16,185,129,0.15), 0 24px 60px -12px rgba(0,0,0,0.6), 0 0 40px rgba(16,185,129,0.28)",
          }}
        >
          <div className="px-4 py-3 border-b border-white/5 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Terminal size={14} className="text-emerald-400" />
              <span className="text-sm font-semibold text-white tracking-tight">
                Dev Ops Console
              </span>
              <span className="text-[9px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-300 border border-emerald-500/25">
                LOCAL
              </span>
            </div>
            <button
              onClick={() => setOpen(false)}
              data-testid="devops-close"
              className="text-slate-400 hover:text-white p-1.5 rounded-md hover:bg-white/5"
            >
              <X size={14} />
            </button>
          </div>

          <div className="p-3 border-b border-white/5 text-[10px] font-mono uppercase tracking-widest">
            <span className="text-slate-500">Signed in as</span>{" "}
            <span className="text-emerald-300">
              {user?.email || "—"}
            </span>{" "}
            <span className="text-slate-500">
              · {user?.subscription_tier || user?.subscription_status || "unauth"}
            </span>
          </div>

          <div className="p-3 flex flex-col gap-2">
            {ACTIONS.map((a) => (
              <button
                key={a.key}
                onClick={a.run}
                disabled={busy === a.key}
                data-testid={`devops-${a.key}`}
                className="w-full text-left px-3 py-2.5 rounded-lg bg-white/[0.03] hover:bg-emerald-500/10 border border-white/5 hover:border-emerald-500/40 transition-colors flex items-start gap-3 disabled:opacity-60"
              >
                <div className="w-8 h-8 rounded-md bg-emerald-500/10 border border-emerald-500/25 flex items-center justify-center shrink-0">
                  <a.icon size={14} className="text-emerald-400" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-white font-medium">{a.label}</div>
                  <div className="text-[10px] text-slate-400 font-mono uppercase tracking-widest">
                    {a.hint}
                  </div>
                </div>
                {busy === a.key && (
                  <span className="text-[10px] font-mono text-emerald-300">…</span>
                )}
              </button>
            ))}
          </div>

          <div className="px-4 py-2 border-t border-white/5 text-[9px] font-mono uppercase tracking-widest text-slate-500 flex items-center justify-between">
            <span>NODE_ENV · {typeof process !== "undefined" ? process.env.NODE_ENV : "?"}</span>
            <span>z-index 10000</span>
          </div>
        </div>
      )}
    </>
  );
}
